/**
 * "Download image" for chat figures (charts and diagrams). The figure is
 * rasterised exactly as rendered — theme colours, fonts, legend — at 2× for
 * crisp slides, on the surface colour behind it so dark-mode text doesn't
 * land on a transparent background.
 */

const MAX_FILE_STEM_LENGTH = 60;

/** A safe file stem from a figure title: "Revenue vs Costs (Q1)" → "revenue-vs-costs-q1". */
export function figureFileStem(title: string | null, fallback: string): string {
  const stem = (title ?? "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\p{Letter}\p{Number}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_FILE_STEM_LENGTH)
    .replace(/-+$/, "");
  return stem.length > 0 ? stem : fallback;
}

/** The first opaque background walking up from the node, else white. */
export function resolveSurfaceColor(node: Element): string {
  let current: Element | null = node;
  while (current) {
    const color = getComputedStyle(current).backgroundColor;
    if (color && color !== "transparent" && !/^rgba\(\s*\d+,\s*\d+,\s*\d+,\s*0\)$/.test(color)) {
      return color;
    }
    current = current.parentElement;
  }
  return "#ffffff";
}

export async function downloadFigureAsPng(node: HTMLElement, fileStem: string): Promise<void> {
  const { toBlob } = await import("html-to-image");
  const blob = await toBlob(node, {
    pixelRatio: 2,
    backgroundColor: resolveSurfaceColor(node),
    // The hover toolbar sits inside the figure; leave it out of the picture.
    filter: (candidate) =>
      !(candidate instanceof Element && candidate.matches("[data-figure-actions]")),
  });
  if (!blob) {
    throw new Error("The figure could not be rendered to an image.");
  }
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${fileStem}.png`;
    anchor.rel = "noopener";
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Revoke after the click has been dispatched; the download keeps its own handle.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
